import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { CheckCircle, CreditCard, ShieldCheck, Receipt } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api, { formatApiError } from '@/lib/api';
import { openCheckout } from '@/lib/razorpay';
import { BrandMark } from '@/pages/LoginPage';

const fmt = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PublicPayPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [paying, setPaying] = useState(false);
  const [done, setDone] = useState(null);

  const load = () => api.get(`/pay/${token}`).then(r => setInfo(r.data)).catch(e => setError(formatApiError(e.response?.data?.detail) || 'Payment link not found'));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const pay = async () => {
    setPaying(true);
    try {
      const { data: order } = await api.post(`/pay/${token}/order`);
      const res = await openCheckout({ ...order, name: info.company_name || 'SMSuitePro', description: `Invoice ${info.invoice_number}` });
      const { data } = await api.post(`/pay/${token}/verify`, res);
      setDone(data);
      await load();
    } catch (err) {
      const msg = err?.response ? formatApiError(err.response?.data?.detail) : err.message;
      if (msg !== 'Payment cancelled') toast.error(msg || 'Payment failed');
    } finally {
      setPaying(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F6FB] flex flex-col items-center px-4 py-10" data-testid="public-pay-page">
      <div className="mb-6"><BrandMark /></div>
      <div className="w-full max-w-md bg-white rounded-2xl border border-[#E4E4E7] shadow-sm overflow-hidden">
        {error ? (
          <div className="p-8 text-center text-sm text-red-600" data-testid="pay-error">{error}</div>
        ) : !info ? (
          <div className="p-8 text-center text-sm text-[#52525B]">Loading…</div>
        ) : (
          <>
            <div className="bg-[#1E3A8A] text-white px-6 py-5">
              <p className="text-[10px] uppercase tracking-[0.14em] text-white/70">{info.company_name || 'Payment request'}</p>
              <p className="text-lg font-bold mt-1 flex items-center gap-2"><Receipt size={18} weight="fill" /> Invoice {info.invoice_number}</p>
              <p className="text-xs text-white/80 mt-0.5">{info.customer_name} · {info.invoice_date}</p>
            </div>
            <div className="px-6 py-5 space-y-3">
              <ul className="divide-y divide-[#F4F4F5]" data-testid="pay-items">
                {info.items.map((it, i) => (
                  <li key={i} className="flex items-center justify-between py-2 text-sm">
                    <span className="text-[#3F3F46]">{it.service_name} {it.quantity > 1 ? <span className="text-[#A1A1AA]">× {it.quantity}</span> : null}</span>
                    <span className="font-mono">{fmt(it.total)}</span>
                  </li>
                ))}
              </ul>
              <div className="rounded-xl bg-[#F8FAFC] border border-[#E4E4E7] p-3 text-sm space-y-1">
                <div className="flex justify-between text-[#52525B]"><span>Bill total</span><span className="font-mono">{fmt(info.total)}</span></div>
                <div className="flex justify-between text-[#52525B]"><span>Paid</span><span className="font-mono">{fmt(info.amount_paid)}</span></div>
                <div className="flex justify-between font-bold text-base pt-1 border-t border-[#E4E4E7]"><span>Amount due</span><span className="font-mono text-[#1E3A8A]" data-testid="pay-due">{fmt(info.due)}</span></div>
              </div>
              {done || info.due <= 0 ? (
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-center" data-testid="pay-success">
                  <CheckCircle size={32} weight="fill" className="text-emerald-500 mx-auto" />
                  <p className="font-semibold text-emerald-700 mt-1">{done ? 'Payment successful' : 'This invoice is fully paid'}</p>
                  {done?.receipt_number && <p className="text-xs text-emerald-700/80 mt-0.5">Receipt {done.receipt_number}</p>}
                </div>
              ) : (
                <Button onClick={pay} disabled={paying || !info.enabled} className="w-full h-11 rounded-xl bg-[#1E3A8A] hover:bg-[#1E40AF] text-white text-base" data-testid="pay-now-btn">
                  <CreditCard size={18} className="mr-2" /> {paying ? 'Opening Razorpay…' : `Pay ${fmt(info.due)}`}
                </Button>
              )}
              <p className="flex items-center justify-center gap-1.5 text-[11px] text-[#A1A1AA]"><ShieldCheck size={13} /> Secured by Razorpay · UPI, cards, net banking</p>
              {info.company_mobile && <p className="text-center text-[11px] text-[#71717A]">Questions? Call {info.company_mobile}</p>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
